import React, { useState, useEffect } from "react";
import "./style/touchless.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../store/store";
import { setLoading, setSelectedLanguage } from "../../store/slices/appSlice";
import { setAuthFlow, setTokens } from "../../store/slices/authSlice";
import { touchlessService } from "../../services/api/touchlessService";
import SplashScreen from "../../components/ui/SplashScreen/splashScreen";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useNewVisitorSession } from "../../hooks/useNewVisitorSession";
import { getCookie } from "../../utils/helpers";
import { ROUTES } from "../../utils/constants";
import { showError } from "../../services/toastservice/toastService";
import { useStrings } from "../../hooks/useStrings";

const Touchless: React.FC = () => {
  const S = useStrings();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const { config, updateConfig } = useAppConfig();
  const { startNewVisitorSession } = useNewVisitorSession();

  // State variables matching Angular component
  const [token, setToken] = useState<string>("");
  const [isEvacuation, setIsEvacuation] = useState<boolean>(false);
  const [isNewQRUrl, setIsNewQRUrl] = useState<boolean>(false);
  const [isSplashScreen, setIsSplashScreen] = useState<boolean>(false);

  useEffect(() => {
    // Extract query parameters - equivalent to Angular route.queryParams.subscribe
    const tokenParam =
      searchParams.get("token") || searchParams.get("tkn") || "";
    const isNewQR = searchParams.has("tkn");

    setToken(tokenParam);
    setIsNewQRUrl(isNewQR);

    if (tokenParam) {
      sessionStorage.setItem("tempToken", `Bearer ${tokenParam}`);
    }
  }, [searchParams]);

  useEffect(() => {
    startNewVisitorSession();
    if (token) {
      initializeComponent();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const initializeComponent = async () => {
    dispatch(setLoading(true));

    try {
      // Step 1: Exchange the QR token for a JWT.
      // Failures here mean an invalid or expired QR code — show that message.
      let authResponse;
      try {
        authResponse = await touchlessService.getAuthenticate(isNewQRUrl, token);
      } catch (authError) {
        console.error("QR authentication error:", authError);
        showError(S.TOUCHLESS.TOAST_INVALID_QR);
        dispatch(setLoading(false));
        navigate("/home");
        return;
      }

      dispatch(setTokens({ accessToken: `Bearer ${authResponse.accessToken}` }));
      dispatch(setAuthFlow('device'));

      // Step 2: Post-auth calls. Any failure here is already toasted by the
      // global interceptor (session expired, network error) — don't double-toast
      // with a generic "Invalid QR Code" message.
      const configResponse = await touchlessService.getOrgConfig();

      if (configResponse) {
        const primaryLang = configResponse.language ?? 'en';
        if (primaryLang !== 'en') {
          const translatedConfig = await touchlessService.getOrgConfig(primaryLang);
          updateConfig(translatedConfig);
        } else {
          updateConfig(configResponse);
        }
        dispatch(setSelectedLanguage(primaryLang));

        await checkIsEvacuationStarted(configResponse.siteId);

        const middlewareService =
          await import("../../services/middleware/middlewareService")
            .then((m) => m.middlewareService)
            .catch(() => null);
        if (middlewareService) {
          middlewareService.setUser({
            id: configResponse.deviceId,
            orgId: configResponse.orgId,
            orgName: configResponse.orgName,
            siteId: configResponse.siteId,
            siteName: configResponse.siteName,
          });
        }

        const nextRoute = configResponse.privacyConsentType
          ? `/${ROUTES.PRIVACY_POLICY}`
          : `/${ROUTES.FULL_NAME}`;

        if (getCookie("visitId")) {
          navigate(`/${ROUTES.SIGN_OUT}`, { replace: true, state: { nextRoute } });
          return;
        }

        navigate(nextRoute);
      } else {
        showError(S.TOUCHLESS.TOAST_INVALID_QR);
        dispatch(setLoading(false));
        navigate("/home");
      }
    } catch (error) {
      // Interceptor already showed the appropriate toast (session expired /
      // network error). Just navigate the user away from the loading screen.
      console.error("Post-authentication error:", error);
      dispatch(setLoading(false));
      navigate("/home");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const checkIsEvacuationStarted = async (siteId: string) => {
    try {
      const response = await touchlessService.checkIsEvacutionStarted(siteId);
      if (
        touchlessService.isNullUndefinedOrBlank(response) &&
        response.data > 0
      ) {
        setIsEvacuation(true);
      }
    } catch (error) {
      console.error("Error checking evacuation status:", error);
    }
  };

  const onTapToStart = () => {
    setIsSplashScreen(false);
  };

  const containerStyle = {
    backgroundColor: "#FFFFFF",
  };

  const screenStyle = {
    backgroundImage: config?.bgImgURI ? `url(${config.bgImgURI})` : "none",
  };

  return (
    <>
      {/* Evacuation Warning */}
      {isEvacuation && (
        <div
          className="topbar-message"
          data-testid="touchless-evacuation-banner"
        >
          <p data-testid="touchless-evacuation-text">
            <i
              className="bi bi-exclamation-triangle-fill me-2 text-yellow"
              data-testid="touchless-evacuation-icon"
            ></i>
            {S.TOUCHLESS.EVACUATION_BANNER}
          </p>
        </div>
      )}

      {/* Main Screen */}
      {!isSplashScreen && (
        <div
          className="main-screen"
          style={containerStyle}
          data-testid="touchless-main-screen"
        >
          <div
            className="screen-outer signin-screen"
            id="touchless"
            style={screenStyle}
            data-testid="touchless-screen-outer"
          >
            <div
              className="screen-header-fixed"
              data-testid="touchless-header-fixed"
            >
              {/* Header content - currently empty in Angular version */}
            </div>

            <div className="screen-inner" data-testid="touchless-screen-inner">
              <div
                className="screen-subinner"
                data-testid="touchless-screen-subinner"
              >
                <div
                  className="screen-header-logo"
                  data-testid="touchless-logo-container"
                >
                  <img
                    src={config?.logoURI}
                    alt="Visitly"
                    data-testid="touchless-logo-image"
                  />
                </div>

                <div
                  className="screen-body"
                  data-testid="touchless-screen-body"
                >
                  <button
                    type="button"
                    className="btn btn-primary btn-padding btn-shadow w-100"
                    id="signIn"
                    onClick={initializeComponent}
                    data-testid="touchless-signin-button"
                    style={{ display: "none" }}
                  >
                    {S.TOUCHLESS.BTN_TAP_TO_SIGNIN}
                    <i
                      className="bi bi-box-arrow-in-right ms-2"
                      data-testid="touchless-signin-icon"
                    ></i>
                  </button>
                  {/* QR Scanner Section */}
                  <button
                    type="button"
                    className="btn btn-white btn-scanner mt-3 d-none"
                    data-testid="touchless-qr-scan-button"
                  >
                    {/* <img
                      src="assets/images/scanner.png"
                      alt="Visitly"
                      data-testid="touchless-qr-scan-image"
                    /> */}
                    <b data-testid="touchless-qr-scan-text">{S.TOUCHLESS.BTN_SCAN_QR_CODE}</b> for
                    Contactless Sign in
                  </button>
                </div>

                <div
                  className="screen-footer"
                  data-testid="touchless-screen-footer"
                >
                  {/* Footer content - currently empty in Angular version */}
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Splash Screen */}
      {isSplashScreen && config && (
        <SplashScreen orgConfig={config} onTapToStart={onTapToStart} />
      )}
    </>
  );
};

export default Touchless;
